import {
  buildInvoice,
  buildSalesOrder,
  invoiceLinesFromOrder,
  reviseSalesOrder,
  setSalesOrderStatus,
  buildPayment,
  closeOrderOp,
  computeLine,
  dispatchDelivery,
  formatQty,
  orderLines,
  normalizeSerial,
  type BuiltInvoice,
  type PaymentMethod,
  type ScaleReading,
} from '@ims/core'
import type { CustomerRow, ProductCodeRow, ProductRow, ProductSerialRow } from '@ims/data'
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  IconButton,
  Input,
  Textarea,
  Kbd,
  Modal,
  PageHeader,
  Select,
  cx,
  useToast,
} from '@ims/ui'
import {
  CheckCircle2,
  ChevronUp,
  Minus,
  PenLine,
  Plus,
  Ellipsis,
  Printer,
  QrCode,
  ShoppingCart,
  Trash2,
} from '@ims/ui/icons'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import {
  CustomerCombobox,
  DateInput,
  LocationSelect,
  MoneyInput,
  ProductPicker,
  QtyInput,
  useLookups,
} from '../components.tsx'
import { NewPartyDialog } from '../party-picker.tsx'
import {
  errorMessage,
  useBatchLabels,
  useEntitlements,
  useFieldGroups,
  useLive,
  useOnline,
  useSettings,
} from '../hooks.ts'
import { settlePaidAhead } from '../lib/allocate.ts'
import { billWithNumber, confirmWithServer, monthInvoiceCount } from '../lib/offline.ts'
import { issueText } from '../lib/sync-fixes.ts'
import {
  loadBatchStock,
  expiryBadge,
  planBatchLines,
  type BatchPlan,
  type BatchWithStock,
} from '../lib/batches.ts'
import {
  aboveMrp,
  cartReducer,
  cartTotals,
  amountPricing,
  belowMinPrice,
  emptyCart,
  missingSerials,
  packPricing,
  quickAmounts,
  stockShortfalls,
  tierPrice,
  type BillDiscountMode,
  type CartLine,
  type PriceTier,
  type StockShortfall,
} from '../lib/cart.ts'
import { CustomItemDialog, canAddCustom } from '../custom-item-dialog.tsx'
import { BillDiscountField, LineDiscountInput } from '../discount-input.tsx'
import {
  percentText,
  readDiscountMode,
  saveDiscountMode,
  type LineDiscountMode,
} from '../lib/discount-mode.ts'
import { money, todayIso } from '../lib/format.ts'
import { paymentQrOf } from '../lib/payment-qr.ts'
import { loadSerials, saleProblem, saleSerialOps } from '../lib/serials.ts'
import { type SerialRow } from '../lib/rows.ts'
import { numberingFor } from '../lib/numbering.ts'
import { loadRecipes } from '../lib/recipes.ts'
import {
  deliveryRow,
  isOpenOrder,
  orderCartLines,
  orderItemRows,
  orderName,
  orderRow,
  orderServiceFor,
  tablesPath,
  backLabel,
} from '../lib/orders.ts'
import {
  addDays,
  KIND_WORDS,
  salesOrderCartLines,
  salesOrderLineRows,
  salesOrderPath,
  salesOrderRow,
  type SalesOrderKind,
} from '../lib/sales-orders.ts'
import { lineVatLabel, registrationOf } from '../lib/registration.ts'
import { useTouchSizes } from '../lib/touch.ts'
import { InvoiceA4, Receipt, type PrintFormat } from '../print.tsx'
import { printBill, useBillMeta } from './order-actions.tsx'
import { PrintDialog } from './print-dialog.tsx'
import { useServerName } from './server-dialog.tsx'
import { PaymentQrCard, PaymentQrReminder, PaymentQrScreen } from '../payment-qr.tsx'
import { ScanButton } from '../scanner.tsx'
import { useCan, useRepo, useStore, useWorkspace } from '../workspace.tsx'

const METHODS: { value: PaymentMethod; label: string; title: string }[] = [
  { value: 'cash', label: 'Cash', title: 'Cash' },
  { value: 'wallet', label: 'Wallet', title: 'Wallet (eSewa/Khalti)' },
  { value: 'bank', label: 'Bank', title: 'Bank transfer' },
  { value: 'card', label: 'Card', title: 'Card' },
]

interface Completed {
  built: BuiltInvoice
  paidPaisa: number
  changePaisa: number
  serverNumbered: boolean
  /** The server's answer, asked for right after saving when online (null: not asked). */
  check: 'checking' | 'ok' | 'unknown' | { code: string } | null
}

/**
 * `doc`: the screen edits a quotation or a sales order instead of making a bill
 * (the same cart, no payment and no stock). Without it, `?from=<id>` makes a
 * bill from one: its lines fill the cart and the bill points back at it.
 */
export interface DocEditor {
  kind: SalesOrderKind
  /** `edit` a draft in place, or `revise` one that has left draft (a new document). */
  mode: 'new' | 'edit' | 'revise'
}

export function QuickBillScreen({ doc }: { doc?: DocEditor } = {}) {
  useTouchSizes()
  const repo = useRepo()
  const { engine } = useStore()
  const settings = useSettings()
  const entitlements = useEntitlements()
  const online = useOnline()
  const lookups = useLookups()
  const { current } = useWorkspace()
  const canSell = useCan('waiter')
  const isManager = useCan('manager')
  const { groups } = useFieldGroups()
  const [tierOverride, setTierOverride] = useState<PriceTier | null>(null)
  const toast = useToast()
  const printNotRecorded = (error: unknown) =>
    toast.error('Printed, but the print was not recorded', { detail: errorMessage(error) })
  const navigate = useNavigate()
  const [cart, dispatch] = useReducer(cartReducer, emptyCart)
  // How empty discount boxes take a number: the last choice on this device.
  const [lineMode, setLineMode] = useState<LineDiscountMode>(() => readDiscountMode('line'))
  const [billPref, setBillPref] = useState<BillDiscountMode>(() => readDiscountMode('bill'))
  const [customer, setCustomer] = useState<CustomerRow | null>(null)
  const tier: PriceTier = tierOverride ?? customer?.price_tier ?? 'retail'
  const chooseCustomer = (c: CustomerRow | null) => {
    setCustomer(c)
    setTierOverride(null)
    dispatch({ type: 'setTier', tier: c?.price_tier ?? 'retail' })
  }
  const [locationId, setLocationId] = useState<string | null>(null)
  const [method, setMethod] = useState<PaymentMethod>('cash')
  // What the customer handed over. `undefined`: nobody has touched the field, so it shows
  // and follows the total. `null`: the cashier emptied it, which stays empty and counts as
  // nothing received (it must not jump back to the total while they are typing).
  const [received, setReceived] = useState<number | null | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [completed, setCompleted] = useState<Completed | null>(null)
  const [newCustomer, setNewCustomer] = useState<string | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [qrShown, setQrShown] = useState(false)
  const [note, setNote] = useState('')
  const [customOpen, setCustomOpen] = useState(false)
  // A quotation's last valid day / an order's expected day, and the quotation's own terms.
  const [docDay, setDocDay] = useState<string | null>(null)
  const [terms, setTerms] = useState('')
  // Who may bill a line without a product (the server checks the same).
  const canCustom = canAddCustom(settings?.custom_items, isManager)
  const scanner = useRef<HTMLInputElement>(null)
  const customerInput = useRef<HTMLInputElement>(null)
  const focusScanner = () => requestAnimationFrame(() => scanner.current?.focus())

  // Checking out a table's order: its items fill the cart and can't be changed here.
  const [query] = useSearchParams()
  const orderId = doc ? null : query.get('order')
  // The quotation or order being edited, revised, or billed (`?from=`).
  const { id: docId } = useParams()
  const fromId = doc ? null : query.get('from')
  const sourceId = doc ? (doc.mode === 'new' ? null : (docId ?? null)) : fromId
  const source = useLive(
    async (r) => (sourceId ? await r.salesOrders.detail(sourceId) : null),
    [sourceId],
    ['sales_orders', 'sales_order_lines', 'sales_invoices', 'sales_invoice_lines'],
  ).data
  const sourceLoaded = useRef<string | null>(null)
  useEffect(() => {
    if (!source || sourceLoaded.current === source.order.id) return
    sourceLoaded.current = source.order.id
    const { order } = source
    // A bill takes what is left to bill; the editor takes every line as it was agreed.
    const lines = invoiceLinesFromOrder(
      salesOrderLineRows(source.lines),
      doc ? {} : source.invoicedByLine,
    )
    void (async () => {
      const [cartLines, buyer] = await Promise.all([
        salesOrderCartLines(repo, lines, !doc),
        order.customer_id ? repo.get('customers', order.customer_id) : null,
      ])
      dispatch({ type: 'load', lines: cartLines })
      setCustomer(buyer)
      if (order.location_id) setLocationId(order.location_id)
      if (doc) {
        setDocDay(order.kind === 'quotation' ? order.valid_until : order.expected_date)
        setNote(order.notes ?? '')
        setTerms(order.terms ?? '')
      }
    })()
  }, [source, doc, repo])
  const orderDetail = useLive(
    async (r) => {
      const detail = orderId ? await r.orders.detail(orderId) : null
      if (!detail) return null
      const table = detail.order.table_id
        ? await r.get('dining_tables', detail.order.table_id)
        : null
      // A takeaway is checked out by its number and name ("#23 Takeaway · Sita").
      const tableName =
        detail.order.service_type === 'dine_in'
          ? (table?.name ?? null)
          : orderName(detail.order, null)
      // A delivery leaves with this checkout, billed to its customer at its address.
      const [delivery, buyer] = await Promise.all([
        detail.order.service_type === 'delivery' ? r.deliveries.forOrder(detail.order.id) : null,
        detail.order.customer_id ? r.get('customers', detail.order.customer_id) : null,
      ])
      return { ...detail, tableName, delivery, buyer }
    },
    [orderId],
    ['orders', 'order_items', 'dining_tables', 'deliveries', 'customers'],
  ).data
  const delivery = orderDetail?.delivery ?? null
  const orderBuyer = orderDetail?.buyer ?? null
  const orderOpen = orderDetail ? isOpenOrder(orderDetail.order) : false
  const orderBill = useMemo(
    () => (orderDetail && orderOpen ? orderLines(orderItemRows(orderDetail.items)) : null),
    [orderDetail, orderOpen],
  )
  useEffect(() => {
    if (!orderBill) return
    let stale = false
    void orderCartLines(repo, orderBill).then((lines) => {
      if (stale) return
      dispatch({ type: 'load', lines })
      // The order's customer (a delivery always has one) is billed unless another is chosen.
      if (orderBuyer) setCustomer((c) => c ?? orderBuyer)
    })
    return () => {
      stale = true
    }
  }, [orderBill, orderBuyer, repo])

  const batchLabels = useBatchLabels(completed?.built.lines.map((l) => l.batch_id) ?? [])
  const serverName = useServerName()
  // The Sale complete dialog's prints: the bill as it is now, with the number the server gave
  // it (`billWithNumber`), not the copy taken when the sale was saved. Waiting for the number
  // shows on the button only when it takes a moment.
  const [gettingNumber, setGettingNumber] = useState<PrintFormat | null>(null)
  // Sale complete's More…: A4, or a PDF to download or share (Print or save).
  const [moreFor, setMoreFor] = useState<string | null>(null)
  const billMeta = useBillMeta()
  const printSale = async (format: PrintFormat) => {
    if (!completed) return
    const { built, paidPaisa, changePaisa } = completed
    const slow = setTimeout(() => setGettingNumber(format), 250)
    try {
      const invoice = await billWithNumber(
        () => repo.get('sales_invoices', built.invoice.id),
        engine,
        online,
        built.invoice,
      )
      // A table's bill prints its table, guests and server (null at the counter).
      const service = await orderServiceFor(repo, invoice, serverName)
      const meta = await billMeta(repo, invoice)
      await printBill(
        repo,
        invoice.tenant_id,
        invoice,
        format,
        (copyLabel) =>
          format === 'receipt' ? (
            <Receipt
              invoice={invoice}
              lines={built.lines}
              settings={settings}
              balancePaisa={null}
              paidPaisa={paidPaisa}
              changePaisa={changePaisa}
              batches={batchLabels}
              copyLabel={copyLabel}
              service={service}
              meta={meta}
            />
          ) : (
            <InvoiceA4
              invoice={invoice}
              lines={built.lines}
              settings={settings}
              balancePaisa={null}
              batches={batchLabels}
              copyLabel={copyLabel}
              service={service}
              meta={meta}
            />
          ),
        printNotRecorded,
      )
    } finally {
      clearTimeout(slow)
      setGettingNumber(null)
    }
  }
  const location = locationId ?? lookups.activeLocations[0]?.id ?? null
  const totals = cartTotals(cart)
  const billMode: BillDiscountMode = cart.billMode ?? billPref
  const modeOfLine = (l: CartLine): LineDiscountMode =>
    l.discountPercentBp != null ? 'percent' : l.discountPaisa ? 'amount' : lineMode
  /** Switches a line between rupees and percent, keeping what its discount comes to. */
  const switchLineMode = (l: CartLine, mode: LineDiscountMode) => {
    setLineMode(mode)
    saveDiscountMode('line', mode)
    const discount = l.discountPaisa ?? 0
    if (mode === 'percent') {
      const gross = Math.round((l.qtyMilli * l.unitPricePaisa) / 1000)
      const bp = discount > 0 && gross > 0 ? Math.round((discount * 10000) / gross) : 0
      dispatch({ type: 'setDiscountPercent', key: l.key, percentBp: bp })
    } else {
      dispatch({ type: 'setDiscount', key: l.key, discountPaisa: discount })
    }
  }
  const switchBillMode = (mode: BillDiscountMode) => {
    setBillPref(mode)
    saveDiscountMode('bill', mode)
    const bill = totals.error ? 0 : totals.billDiscountPaisa
    if (mode === 'percent') {
      const base = totals.afterLinesPaisa
      dispatch({
        type: 'setBillPercent',
        percentBp: bill > 0 && base > 0 ? Math.round((bill * 10000) / base) : 0,
      })
    } else if (mode === 'final') {
      dispatch({ type: 'setBillFinal', totalPaisa: bill > 0 ? totals.totalPaisa : null })
    } else {
      dispatch({ type: 'setDocumentDiscount', discountPaisa: bill })
    }
  }
  const pct = (part: number, of: number) =>
    of > 0 ? percentText(Math.round((part * 10000) / of)) : '0'
  const billHint =
    totals.error || totals.billDiscountPaisa === 0
      ? undefined
      : billMode === 'percent'
        ? `= ${money(totals.billDiscountPaisa, settings)} off ${money(totals.afterLinesPaisa, settings)}`
        : billMode === 'final'
          ? `Discount ${money(totals.billDiscountPaisa, settings)} (${pct(totals.billDiscountPaisa, totals.afterLinesPaisa)}%)`
          : `= ${pct(totals.billDiscountPaisa, totals.afterLinesPaisa)}% of ${money(totals.afterLinesPaisa, settings)}`
  const batchKey = [
    ...new Set(cart.lines.filter((l) => l.trackBatches && l.productId).map((l) => l.productId!)),
  ].join(',')
  const batchProductIds = useMemo(() => (batchKey ? batchKey.split(',') : []), [batchKey])
  const batchStockData = useLive(
    (r) => loadBatchStock(r, batchProductIds, location),
    [batchKey, location],
    ['batches', 'stock_movements'],
  ).data
  const batchStock = useMemo(
    () => batchStockData ?? new Map<string, BatchWithStock[]>(),
    [batchStockData],
  )
  // Stock at the selling location, to warn before a line takes it below zero. An order
  // is checked against what other confirmed orders have not already promised.
  const orderDoc = doc?.kind === 'order'
  const stockAtLocation = useLive(
    async (r) => {
      const levels = new Map(
        (await r.stock.levels(location ? { locationId: location } : {})).map(
          (l) => [l.product_id, l.qty_milli] as const,
        ),
      )
      if (!orderDoc) return levels
      const promised = await r.stock.committed({
        locationId: location ?? undefined,
        exceptOrderId: sourceId ?? undefined,
      })
      for (const [productId, held] of Object.entries(promised))
        levels.set(productId, (levels.get(productId) ?? 0) - held)
      return levels
    },
    [location, orderDoc, sourceId],
    [
      'stock_movements',
      'sales_orders',
      'sales_order_lines',
      'sales_invoices',
      'sales_invoice_lines',
    ],
  ).data
  // A quotation promises nothing, so it is not checked against stock.
  const shortfalls = useMemo(
    () =>
      stockAtLocation && doc?.kind !== 'quotation'
        ? stockShortfalls(cart.lines, stockAtLocation)
        : [],
    [cart.lines, stockAtLocation, doc],
  )
  const [shortfallAsk, setShortfallAsk] = useState<StockShortfall[] | null>(null)
  const expiredSale = settings?.expired_sale ?? 'warn'
  const batchPlan = useMemo(
    () => planBatchLines(cart.lines, batchStock, todayIso(), expiredSale),
    [cart.lines, batchStock, expiredSale],
  )
  // A delivery is cash on delivery unless paid now: nothing is received by default.
  const untouched = delivery ? 0 : totals.totalPaisa
  const paid = received === undefined ? untouched : (received ?? 0)
  const change = Math.max(0, paid - totals.totalPaisa)
  const credit = Math.max(0, totals.totalPaisa - paid)

  // Tax rate and unit come straight from the database: a scan right after the
  // page opens must not bill without VAT because lookups are still loading.
  const addProduct = async (
    p: ProductRow,
    pack: ProductCodeRow | null = null,
    serial: ProductSerialRow | null = null,
    scale: ScaleReading | null = null,
  ) => {
    const problem = serial ? saleProblem(serial.serial, serial) : null
    if (problem) {
      toast.error(problem)
      return
    }
    // A scanned code finds any product; an ingredient is not for sale (the server refuses it too).
    if (!p.for_sale) {
      toast.error(`${p.name} is an ingredient, not something you sell.`)
      return
    }
    const taxId = p.tax_rate_id ?? settings?.default_tax_rate_id ?? null
    const [tax, unit] = await Promise.all([
      taxId ? repo.get('tax_rates', taxId) : Promise.resolve(null),
      p.unit_id ? repo.get('units', p.unit_id) : Promise.resolve(null),
    ])
    // A pack barcode sells the pack's quantity; with its own price, exactly that price.
    const packPrice =
      pack?.sell_price_paisa != null ? packPricing(pack.qty_milli, pack.sell_price_paisa) : null
    const unitPrice = tierPrice(p.sell_price_paisa, p.wholesale_price_paisa, tier)
    // A scale sticker with a price sells exactly that amount of the product.
    const stickerPrice =
      scale?.amountPaisa !== undefined ? amountPricing(unitPrice, scale.amountPaisa) : null
    if (scale?.amountPaisa !== undefined && !stickerPrice) {
      toast.error(`${p.name} has no price, so the sticker's amount can't be sold`)
      return
    }
    const fixed = packPrice ?? stickerPrice
    dispatch({
      type: 'add',
      line: {
        productId: p.id,
        description: pack?.label ? `${p.name} (${pack.label})` : p.name,
        unitPricePaisa: packPrice?.unitPricePaisa ?? unitPrice,
        priceIncludesTax: p.price_includes_tax,
        taxRateBp: tax?.rate_bp ?? 0,
        taxRateId: tax?.id ?? null,
        discountPaisa: fixed?.discountPaisa ?? 0,
        trackStock: p.track_stock,
        unitSymbol: unit?.symbol ?? '',
        allowsDecimal: unit?.allows_decimal ?? false,
        mrpPaisa: p.mrp_paisa,
        // A quotation or order names no batch and no serial: the bill made from it does.
        trackBatches: !doc && p.track_batches,
        trackSerials: !doc && p.track_serials,
        retailPricePaisa: p.sell_price_paisa,
        wholesalePricePaisa: p.wholesale_price_paisa,
        minPricePaisa: p.min_price_paisa,
        fixedPrice: fixed !== null,
      },
      qtyMilli: pack?.qty_milli ?? scale?.qtyMilli ?? stickerPrice?.qtyMilli,
      // Each weighed item is its own line.
      separate: fixed !== null || scale !== null,
      ...(serial ? { serial: serial.serial } : {}),
    })
    setReceived(undefined)
  }

  /** A serial typed into a line: must be a unit of that product that isn't sold. */
  const addSerial = async (
    key: string,
    productId: string,
    text: string,
  ): Promise<string | null> => {
    if (!current) return null
    const serial = normalizeSerial(text)
    if (!serial) return null
    const found = (await loadSerials(repo, current.tenant_id, productId, [serial])).get(serial)
    const problem = saleProblem(serial, found ?? null)
    if (!problem) dispatch({ type: 'addSerial', key, serial })
    return problem
  }

  /** `sellShort`: the cashier confirmed selling more than is in stock. */
  const complete = useCallback(
    async (sellShort = false) => {
      if (!settings || !current || cart.lines.length === 0 || totals.error) return
      if (credit > 0 && !customer) {
        toast.error('Choose a customer to sell on credit (amount received is less than the total).')
        return
      }
      const missing = missingSerials(cart.lines)
      if (missing) {
        toast.error(missing)
        return
      }
      // The plan's bills for the month, as far as this device knows. The server
      // still accepts a bill made offline before the device could know.
      const monthLimit = entitlements?.limits.max_invoices_per_month ?? null
      if (monthLimit !== null && (await monthInvoiceCount(repo)) >= monthLimit) {
        toast.error(
          `This month’s ${monthLimit} bills on your plan are used. Upgrade the plan (Settings → Subscription) to keep billing.`,
        )
        return
      }
      // More than the location has? Staff are stopped when the business doesn't allow
      // it (managers may go ahead); otherwise the cashier confirms. Checked on this
      // device only: the server never refuses a sale that happened at the counter.
      if (!sellShort) {
        const levels = await repo.stock.levels(location ? { locationId: location } : {})
        const short = stockShortfalls(
          cart.lines,
          new Map(levels.map((l) => [l.product_id, l.qty_milli] as const)),
        )
        if (short.length > 0) {
          if (!settings.allow_negative_stock && !isManager) {
            toast.error(
              `Not enough stock: ${short.map((x) => `${x.description} (${formatQty(Math.max(0, x.haveMilli))} left)`).join(', ')}. Receive the stock first, or ask a manager.`,
            )
            return
          }
          setShortfallAsk(short)
          return
        }
      }
      setBusy(true)
      try {
        const today = todayIso()
        // Each unit is sold once: check the register as it is right now.
        const units = new Map<string, SerialRow | null>()
        const warranty = new Map<string, number | null>()
        for (const l of cart.lines) {
          if (!l.trackSerials || !l.productId || !l.serials?.length) continue
          const found = await loadSerials(repo, current.tenant_id, l.productId, l.serials)
          for (const [serial, row] of found) {
            const problem = saleProblem(serial, row)
            if (problem) {
              toast.error(problem)
              return
            }
            units.set(serial, row)
          }
          warranty.set(
            l.productId,
            (await repo.get('products', l.productId))?.warranty_months ?? null,
          )
        }
        // Batches are chosen from the stock as it is right now.
        const plan = planBatchLines(
          cart.lines,
          await loadBatchStock(repo, batchProductIds, location),
          today,
          settings.expired_sale,
        )
        if (plan.blocked) {
          toast.error(`${plan.blocked}. Choose another batch or remove the item.`)
          return
        }
        // The order as it is right now: another device may have added to it or closed it.
        const order = orderId ? await repo.orders.detail(orderId) : null
        if (orderId) {
          if (!order || !isOpenOrder(order.order)) {
            toast.error('This order is already closed.')
            return
          }
          if (
            JSON.stringify(orderLines(orderItemRows(order.items))) !== JSON.stringify(orderBill)
          ) {
            toast.error('The order changed. Check the bill and complete it again.')
            return
          }
        }
        // The delivery as it is now: it leaves with this bill, so it must still be waiting.
        const leaving = orderId ? await repo.deliveries.forOrder(orderId) : null
        if (leaving && leaving.status !== 'pending') {
          toast.error('This delivery has already left.')
          return
        }
        // The quotation or order as it is right now: another device may have closed it.
        const from = fromId ? await repo.salesOrders.detail(fromId) : null
        if (fromId) {
          const open = from && ['sent', 'accepted', 'confirmed'].includes(from.order.status)
          if (!from || !open) {
            toast.error('This document is a draft or already closed, so it cannot be billed.')
            return
          }
        }
        const numbering = await numberingFor(repo, settings, 'sales_invoice', today)
        const recipes = await loadRecipes(
          repo,
          plan.lines.map((l) => l.productId),
        )
        const ctx = { tenantId: current.tenant_id, now: new Date().toISOString() }
        const built = buildInvoice(ctx, {
          status: 'finalized',
          invoiceDate: today,
          locationId: location,
          currency: settings.currency,
          customer: customer
            ? {
                id: customer.id,
                name: customer.name,
                pan: customer.pan_vat_number,
                // A delivery's bill shows where it went.
                address: leaving?.address ?? customer.address,
              }
            : null,
          // A dish takes its ingredients out of stock, by its recipe as it is now.
          lines: plan.lines.map((l) => ({
            ...l,
            components: l.productId ? (recipes.get(l.productId) ?? null) : null,
          })),
          documentDiscountPaisa: totals.billDiscountPaisa,
          roundOff: 'nearest_rupee',
          notes: note.trim() || null,
          numbering,
          salesOrderId: from?.order.id ?? null,
        })
        const allocated = Math.min(paid, built.invoice.total_paisa)
        const ops = [
          ...built.ops,
          ...saleSerialOps(ctx, built.lines, units, today, (id) => warranty.get(id) ?? null),
        ]
        if (allocated > 0) {
          ops.push(
            ...buildPayment(ctx, {
              customerId: customer?.id ?? null,
              amountPaisa: allocated,
              method,
              paymentDate: today,
              allocations: [{ invoiceId: built.invoice.id, amountPaisa: allocated }],
            }).ops,
          )
        }
        // Staff can't go below a minimum price when the business blocks it (the
        // server checks the same, including each line's share of a bill discount).
        if (settings.min_price_rule === 'block' && !isManager) {
          const mins = new Map(cart.lines.map((l) => [l.productId, l.minPricePaisa]))
          const low = built.lines.find(
            (l) =>
              belowMinPrice({
                qtyMilli: l.qty_milli,
                unitPricePaisa: l.unit_price_paisa,
                discountPaisa: l.discount_paisa,
                minPricePaisa: mins.get(l.product_id) ?? null,
              }) !== null,
          )
          if (low) {
            toast.error(
              `${low.description} is below its minimum price. Ask a manager to approve it.`,
            )
            return
          }
        }
        // The order closes in the same tx as its invoice; a delivery leaves with it.
        if (order) ops.push(closeOrderOp(ctx, orderRow(order.order), built.invoice.id))
        if (leaving) ops.push(...dispatchDelivery(ctx, deliveryRow(leaving)).ops)
        // Billing a quotation is the customer saying yes.
        if (from?.order.kind === 'quotation' && from.order.status === 'sent')
          ops.push(...setSalesOrderStatus(ctx, salesOrderRow(from.order), 'accepted').ops)
        const { txId } = await repo.commit(ops)
        // Money the customer paid ahead comes off this bill: its own commit after the sale, so
        // a refusal (another device used the same money) never costs the sale.
        if (customer)
          void settlePaidAhead(repo, ctx, customer.id).catch((error: unknown) =>
            toast.error(errorMessage(error)),
          )
        const asking = online
        setCompleted({
          built,
          paidPaisa: paid,
          changePaisa: change,
          serverNumbered: numbering.serverNumbered,
          check: asking ? 'checking' : null,
        })
        if (asking)
          void confirmWithServer(engine, txId).then((answer) =>
            setCompleted((c) =>
              c && c.built.invoice.id === built.invoice.id
                ? { ...c, check: typeof answer === 'string' ? answer : { code: answer.code } }
                : c,
            ),
          )
        dispatch({ type: 'clear' })
        setSheetOpen(false)
        setCustomer(null)
        setTierOverride(null)
        setReceived(undefined)
        setNote('')
      } catch (error) {
        toast.error(errorMessage(error))
      } finally {
        setBusy(false)
      }
    },
    [
      settings,
      entitlements,
      isManager,
      current,
      cart,
      totals.billDiscountPaisa,
      totals.error,
      credit,
      customer,
      repo,
      location,
      paid,
      change,
      method,
      toast,
      engine,
      online,
      batchProductIds,
      note,
      orderId,
      orderBill,
      fromId,
    ],
  )

  const docWords = doc ? KIND_WORDS[doc.kind] : null
  const docDayDefault =
    doc?.kind === 'quotation' && settings ? addDays(todayIso(), settings.quote_valid_days) : ''
  const docDayValue = docDay ?? docDayDefault
  /** Saves the quotation or order: a draft, or numbered and ready to hand over. */
  const saveDoc = useCallback(
    async (asDraft: boolean) => {
      if (!doc || !settings || !current || cart.lines.length === 0 || totals.error) return
      if (!customer) {
        toast.error(`Choose the customer this ${doc.kind} is for.`)
        return
      }
      setBusy(true)
      try {
        const today = todayIso()
        const status = asDraft ? 'draft' : doc.kind === 'quotation' ? 'sent' : 'confirmed'
        const ctx = { tenantId: current.tenant_id, now: new Date().toISOString() }
        const input = {
          status,
          docDate: today,
          validUntil: docDayValue || null,
          expectedDate: docDayValue || null,
          customer: {
            id: customer.id,
            name: customer.name,
            pan: customer.pan_vat_number,
            address: customer.address,
          },
          locationId: location,
          currency: settings.currency,
          lines: cart.lines,
          documentDiscountPaisa: totals.billDiscountPaisa,
          roundOff: 'nearest_rupee',
          notes: note,
          terms,
          numbering: asDraft
            ? undefined
            : await numberingFor(repo, settings, KIND_WORDS[doc.kind].doc, today),
        } as const
        // The stored document as it is right now: another device may have sent or closed it.
        const stored = doc.mode === 'new' || !docId ? null : await repo.salesOrders.detail(docId)
        if (doc.mode !== 'new' && !stored) {
          toast.error(`This ${doc.kind} no longer exists.`)
          return
        }
        if (doc.mode === 'edit' && stored?.order.status !== 'draft') {
          toast.error(`This ${doc.kind} has left draft. Revise it instead.`)
          return
        }
        const built =
          doc.mode === 'revise' && stored
            ? reviseSalesOrder(ctx, salesOrderRow(stored.order), input)
            : buildSalesOrder(
                ctx,
                {
                  ...input,
                  kind: doc.kind,
                  id: stored?.order.id,
                  sourceId: stored?.order.source_id,
                },
                stored?.lines ?? [],
              )
        // Staff can't quote below a minimum price when the business blocks it (the server
        // checks the same).
        if (settings.min_price_rule === 'block' && !isManager) {
          const mins = new Map(cart.lines.map((l) => [l.productId, l.minPricePaisa]))
          const low = built.lines.find(
            (l) =>
              belowMinPrice({
                qtyMilli: l.qty_milli,
                unitPricePaisa: l.unit_price_paisa,
                discountPaisa: l.discount_paisa,
                minPricePaisa: mins.get(l.product_id) ?? null,
              }) !== null,
          )
          if (low) {
            toast.error(
              `${low.description} is below its minimum price. Ask a manager to approve it.`,
            )
            return
          }
        }
        await repo.commit(built.ops)
        toast.success(asDraft ? 'Draft saved' : `${KIND_WORDS[doc.kind].one} saved`)
        navigate(salesOrderPath(built.order))
      } catch (error) {
        toast.error(errorMessage(error))
      } finally {
        setBusy(false)
      }
    },
    [
      doc,
      docId,
      settings,
      current,
      cart,
      totals.billDiscountPaisa,
      totals.error,
      customer,
      docDayValue,
      location,
      note,
      terms,
      repo,
      isManager,
      toast,
      navigate,
    ],
  )

  // Counter keys: F9 completes the sale, F2 goes to the scanner, F4 to the customer,
  // F6 adds a custom item.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F9') {
        e.preventDefault()
        if (doc) void saveDoc(false)
        else void complete()
      } else if (e.key === 'F2') {
        e.preventDefault()
        scanner.current?.focus()
      } else if (e.key === 'F4') {
        e.preventDefault()
        customerInput.current?.focus()
      } else if (
        e.key === 'F6' &&
        canCustom &&
        !orderId &&
        !document.querySelector('[role="dialog"]')
      ) {
        e.preventDefault()
        setCustomOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [complete, saveDoc, doc, canCustom, orderId])

  if (!canSell) {
    return (
      <EmptyState
        icon={<ShoppingCart className="size-6" />}
        title="Billing needs the staff role"
        description="Ask an admin to change your role."
      />
    )
  }

  const removeLine = (line: CartLine) => {
    const index = cart.lines.indexOf(line)
    dispatch({ type: 'remove', key: line.key })
    toast.info(`Removed ${line.description}`, {
      label: 'Undo',
      onClick: () => dispatch({ type: 'restore', line, index }),
    })
  }
  const step = (line: CartLine, by: number) => {
    const next = line.qtyMilli + by * 1000
    if (next <= 0) removeLine(line)
    else dispatch({ type: 'setQty', key: line.key, qtyMilli: next })
  }
  const units = cart.lines.reduce((sum, l) => sum + l.qtyMilli, 0) / 1000
  const totalText = money(totals.totalPaisa, settings)
  // The business's payment QR shows while its method is chosen (never on a quotation).
  const payQr = doc ? null : paymentQrOf(settings)
  const qrOn = !!payQr && method === payQr.method && cart.lines.length > 0 && totals.totalPaisa > 0
  // What the customer is asked for: the whole bill, or the part typed as received.
  const qrPaisa = paid > 0 ? Math.min(paid, totals.totalPaisa) : totals.totalPaisa
  const qrAmount = money(qrPaisa, settings)
  // The bill under the camera while a counter scans: newest first, so the item just read
  // is in view, with its amount and a way to take it off again.
  const scanCart = {
    list:
      cart.lines.length === 0 ? (
        <p className="px-5 py-6 text-center text-sm text-muted">Scanned items show here.</p>
      ) : (
        <ul aria-label="Scanned items" className="divide-y divide-border">
          {[...cart.lines].reverse().map((l) => (
            <li key={l.key} className="relative flex items-center gap-3 px-5 py-2.5">
              {l.key === cart.lastKey && (
                <span
                  key={l.qtyMilli}
                  aria-hidden
                  className="pointer-events-none absolute inset-0 animate-flash"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">{l.description}</div>
                <div className="text-xs text-muted tabular">
                  {[formatQty(l.qtyMilli), l.unitSymbol].filter(Boolean).join(' ')} ·{' '}
                  {money(l.unitPricePaisa, settings)} each
                </div>
              </div>
              <span className="font-semibold tabular">
                {(() => {
                  try {
                    return money(computeLine(l).totalPaisa, settings)
                  } catch {
                    return '—'
                  }
                })()}
              </span>
              <IconButton label={`Remove ${l.description}`} onClick={() => removeLine(l)}>
                <Trash2 className="size-4" />
              </IconButton>
            </li>
          ))}
        </ul>
      ),
    summary: (
      <div className="leading-tight">
        <div className="text-xs text-muted">
          {cart.lines.length} {cart.lines.length === 1 ? 'line' : 'lines'}
        </div>
        <div className="font-semibold tabular">Total {totalText}</div>
      </div>
    ),
  }
  const completeLabel = docWords
    ? `Save ${docWords.one.toLowerCase()} · ${totalText}`
    : `Complete sale · ${totalText}`

  const checkout = (
    <>
      <Field label="Customer">
        {(id) => (
          <CustomerCombobox
            id={id}
            inputRef={customerInput}
            value={customer}
            onChange={chooseCustomer}
            onAddNew={(name) => setNewCustomer(name)}
          />
        )}
      </Field>
      {groups.has('wholesale') && (
        <Checkbox
          label="Wholesale prices"
          checked={tier === 'wholesale'}
          onChange={(e) => {
            const next: PriceTier = e.target.checked ? 'wholesale' : 'retail'
            setTierOverride(next)
            dispatch({ type: 'setTier', tier: next })
          }}
        />
      )}
      {lookups.activeLocations.length > 1 && doc?.kind !== 'quotation' && (
        <Field label={doc ? 'Supply from' : 'Sell from'}>
          {(id) => (
            <LocationSelect
              id={id}
              locations={lookups.activeLocations}
              value={location}
              onChange={setLocationId}
            />
          )}
        </Field>
      )}
      <BillDiscountField
        label={doc ? 'Discount' : 'Bill discount'}
        mode={billMode}
        amountPaisa={cart.documentDiscountPaisa}
        percentBp={cart.billPercentBp ?? null}
        finalPaisa={cart.billFinalPaisa ?? null}
        hint={billHint}
        onMode={switchBillMode}
        onAmount={(p) => dispatch({ type: 'setDocumentDiscount', discountPaisa: p })}
        onPercent={(bp) => dispatch({ type: 'setBillPercent', percentBp: bp })}
        onFinal={(p) => dispatch({ type: 'setBillFinal', totalPaisa: p })}
      />

      {doc && settings && docDayValue && (
        <DateInput
          label={doc.kind === 'quotation' ? 'Valid until' : 'Expected on'}
          value={docDayValue}
          onChange={setDocDay}
          calendar={settings.date_display === 'ad' ? 'ad' : 'bs'}
        />
      )}
      {doc?.kind === 'order' && !docDayValue && (
        <Button variant="ghost" size="sm" onClick={() => setDocDay(todayIso())}>
          Add an expected date
        </Button>
      )}
      <Field label={docWords ? `Note on the ${docWords.one.toLowerCase()}` : 'Note on the bill'}>
        {(id) => (
          <Input
            id={id}
            maxLength={300}
            placeholder={doc ? 'Optional, printed on it' : 'Optional, printed on the bill'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        )}
      </Field>
      {doc?.kind === 'quotation' && (
        <Field label="Terms">
          {(id) => (
            <Textarea
              id={id}
              rows={3}
              maxLength={1000}
              placeholder={settings?.quote_terms || 'Optional: delivery, payment, warranty…'}
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
            />
          )}
        </Field>
      )}

      <dl className="space-y-1.5 border-t border-dashed border-border pt-4 text-sm">
        <Row label="Subtotal" value={money(totals.subtotalPaisa, settings)} />
        {totals.discountPaisa > 0 && (
          <Row
            label="Discount"
            value={`−${money(totals.discountPaisa, settings)}`}
            sub={
              totals.lineDiscountPaisa > 0 && totals.billDiscountPaisa > 0
                ? `Lines ${money(totals.lineDiscountPaisa)} · Bill${billMode === 'percent' && cart.billPercentBp ? ` ${percentText(cart.billPercentBp)}%` : ''} ${money(totals.billDiscountPaisa)}`
                : undefined
            }
          />
        )}
        <Row label="Taxable" value={money(totals.taxablePaisa, settings)} />
        <Row label="VAT" value={money(totals.taxPaisa, settings)} />
        {totals.roundOffPaisa !== 0 && (
          <Row label="Round off" value={money(totals.roundOffPaisa, settings)} />
        )}
        <div className="flex items-baseline justify-between border-t border-dashed border-border pt-2">
          <dt className="font-semibold">Total</dt>
          <dd className="text-metric">{totalText}</dd>
        </div>
      </dl>
      {totals.error && <p className="text-sm text-danger">{totals.error}</p>}

      {!doc && (
        <>
          <div>
            <div id="payment-label" className="mb-1.5 block text-sm font-medium">
              Payment
            </div>
            <div
              role="radiogroup"
              aria-labelledby="payment-label"
              className="grid grid-cols-4 gap-1.5"
            >
              {METHODS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  role="radio"
                  aria-checked={method === m.value}
                  title={m.title}
                  onClick={() => setMethod(m.value)}
                  className={cx(
                    'h-10 rounded-xl border text-sm font-semibold transition-colors pos:h-12',
                    method === m.value
                      ? 'border-primary bg-primary-light text-primary ring-1 ring-primary'
                      : 'border-border hover:bg-soft',
                  )}
                >
                  {payQr?.method === m.value && (
                    <QrCode className="mr-1 inline size-4 align-[-3px]" aria-hidden />
                  )}
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          {qrOn && payQr && (
            <PaymentQrCard qr={payQr} amount={qrAmount} onShow={() => setQrShown(true)} />
          )}
          <div>
            <Field label="Received">
              {(id) => (
                <MoneyInput
                  id={id}
                  paisa={received === undefined ? untouched : received}
                  onValue={setReceived}
                />
              )}
            </Field>
            {totals.totalPaisa > 0 && (
              <div className="mt-2 flex gap-1.5">
                {quickAmounts(totals.totalPaisa).map((a, i) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setReceived(a)}
                    className={cx(
                      'h-8 flex-1 rounded-xl border text-xs font-semibold transition-colors',
                      paid === a ? 'border-primary text-primary' : 'border-border hover:bg-soft',
                    )}
                  >
                    {i === 0 ? 'Exact' : money(a, settings).replace(/\.00$/, '')}
                  </button>
                ))}
              </div>
            )}
          </div>
          {change > 0 && (
            <p className="flex justify-between rounded-xl bg-success-light px-3 py-2 text-sm font-semibold text-success">
              <span>Change due</span>
              <span className="tabular">{money(change, settings)}</span>
            </p>
          )}
          {credit > 0 && cart.lines.length > 0 && (
            <p className="rounded-xl bg-warning-light px-3 py-2 text-sm text-warning">
              {money(credit, settings)} will be added to the customer’s balance.
            </p>
          )}
        </>
      )}

      <Button
        size="lg"
        className="w-full"
        loading={busy}
        disabled={cart.lines.length === 0 || !!totals.error}
        onClick={() => void (doc ? saveDoc(false) : complete())}
      >
        {completeLabel}
        <span className="ml-1 hidden lg:inline">
          <Kbd className="border-on-fill/40 bg-transparent text-on-fill">F9</Kbd>
        </span>
      </Button>
      {qrOn && <PaymentQrReminder />}
      {doc && doc.mode !== 'revise' && (
        <Button
          variant="secondary"
          className="w-full"
          disabled={busy || cart.lines.length === 0 || !!totals.error}
          onClick={() => void saveDoc(true)}
        >
          Save as draft
        </Button>
      )}
    </>
  )

  return (
    <div className="pb-24 lg:pb-0">
      <PageHeader
        title={
          docWords
            ? doc?.mode === 'new'
              ? `New ${docWords.one.toLowerCase()}`
              : doc?.mode === 'edit'
                ? `Edit ${docWords.one.toLowerCase()}`
                : `Revise ${source?.order.number ?? docWords.one.toLowerCase()}`
            : orderId
              ? `Checkout${orderDetail?.tableName ? ` · ${orderDetail.tableName}` : ''}`
              : fromId
                ? `Bill ${source?.order.number ?? ''}`.trim()
                : 'Quick bill'
        }
        subtitle={
          docWords
            ? doc?.mode === 'revise'
              ? 'Saving makes a new one and retires the old one. Nothing leaves stock.'
              : 'Scan or search products, then save. Nothing leaves stock.'
            : orderId
              ? 'Take the payment. F9 completes the sale and frees the table.'
              : fromId
                ? 'Lower a quantity to bill part of it. F9 completes the sale.'
                : 'Scan or search products. F9 completes the sale.'
        }
        actions={
          <div className="hidden items-center gap-3 text-xs text-muted xl:flex">
            {!orderId && (
              <span>
                <Kbd>F2</Kbd> scan
              </span>
            )}
            <span>
              <Kbd>F4</Kbd> customer
            </span>
            {canCustom && !orderId && (
              <span>
                <Kbd>F6</Kbd> custom item
              </span>
            )}
            <span>
              <Kbd>F9</Kbd> {doc ? 'save' : 'complete'}
            </span>
          </div>
        }
      />
      <div className="grid items-start gap-4 lg:grid-cols-[1fr_360px]">
        <Card className="p-0">
          {source && (
            <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3 text-sm">
              <span className="text-muted">
                {doc
                  ? `${KIND_WORDS[source.order.kind].one} ${source.order.number ?? '(draft)'}`
                  : `From ${KIND_WORDS[source.order.kind].one.toLowerCase()} ${source.order.number ?? ''}: what is left to bill.`}
              </span>
              <Link
                to={salesOrderPath(source.order)}
                className="shrink-0 font-medium text-primary hover:underline"
              >
                Back to the {KIND_WORDS[source.order.kind].one.toLowerCase()}
              </Link>
            </div>
          )}
          {orderId && (
            <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3 text-sm">
              <span className="text-muted">
                {orderDetail && !orderOpen
                  ? 'This order is already closed.'
                  : 'To add or cancel items, go back to the table.'}
              </span>
              <Link
                to={orderOpen ? `/tables/${orderId}` : tablesPath(orderDetail?.order)}
                className="shrink-0 font-medium text-primary hover:underline"
              >
                {orderOpen ? 'Back to the table' : backLabel(orderDetail?.order)}
              </Link>
            </div>
          )}
          <div
            className={cx('flex items-start gap-2 border-b border-border p-4', orderId && 'hidden')}
          >
            <div className="min-w-0 flex-1">
              <ProductPicker
                inputRef={scanner}
                onPick={(p, pack, serial, scale) =>
                  void addProduct(p, pack, serial ?? null, scale ?? null)
                }
                autoFocus={!orderId}
                forSale
                scan="continuous"
                scanCart={scanCart}
                addNew="product"
              />
            </div>
            {canCustom && (
              <Button
                variant="secondary"
                className="h-11 shrink-0"
                icon={<PenLine className="size-4" />}
                title="A line typed by hand, without a product (F6)"
                onClick={() => setCustomOpen(true)}
              >
                <span className="hidden sm:inline">Custom item</span>
                <span className="sr-only sm:hidden">Custom item</span>
              </Button>
            )}
          </div>
          {batchPlan.warnings.length > 0 && (
            <div className="border-b border-border bg-warning-light px-4 py-2 text-xs text-warning">
              {batchPlan.warnings.map((w) => (
                <div key={w}>{w}</div>
              ))}
            </div>
          )}
          {batchPlan.blocked && (
            <div className="border-b border-border bg-danger-light px-4 py-2 text-xs font-medium text-danger">
              {batchPlan.blocked}
            </div>
          )}
          {cart.lines.length === 0 ? (
            <EmptyState
              icon={<ShoppingCart className="size-6" />}
              title={
                orderId || fromId ? 'Nothing to bill' : doc ? 'Nothing added yet' : 'Cart is empty'
              }
              description={
                orderId
                  ? 'This order has no items, or it is already closed.'
                  : fromId && source
                    ? 'Everything on it has been billed. Add products to bill more.'
                    : canCustom
                      ? 'Scan a barcode, or type a product name or SKU and press Enter. For something that is not a product, add a custom item.'
                      : 'Scan a barcode, or type a product name or SKU and press Enter.'
              }
            />
          ) : (
            <fieldset disabled={!!orderId} className="min-w-0">
              <ul className="divide-y divide-border">
                {cart.lines.map((l) => (
                  <li
                    key={l.key}
                    className="relative grid grid-cols-[minmax(7.5rem,1.2fr)_1fr_1fr] items-center gap-2 p-4 sm:grid-cols-[1fr_140px_120px_110px_auto] sm:gap-3 lg:grid-cols-[minmax(7.5rem,1.2fr)_1fr_1fr] lg:gap-2 xl:grid-cols-[1fr_140px_120px_110px_auto] xl:gap-3"
                  >
                    {l.key === cart.lastKey && (
                      <span
                        key={l.qtyMilli}
                        aria-hidden
                        className="pointer-events-none absolute inset-0 animate-flash"
                      />
                    )}
                    <div className="col-span-2 min-w-0 sm:col-span-1 lg:col-span-2 xl:col-span-1">
                      <div className="font-medium">
                        {l.description}
                        {!l.productId && <Badge className="ml-2 align-middle">Custom</Badge>}
                      </div>
                      <div className="text-xs text-muted">
                        {[
                          l.taxRateBp
                            ? `${lineVatLabel(registrationOf(settings), l.taxRateBp)}${l.priceIncludesTax ? ' incl.' : ''}`
                            : lineVatLabel(registrationOf(settings), 0),
                          l.mrpPaisa != null && `MRP ${money(l.mrpPaisa, settings)}`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                      {(() => {
                        const short = shortfalls.find((x) => x.productId === l.productId)
                        return (
                          short && (
                            <div className="text-xs font-medium text-warning">
                              {short.haveMilli > 0
                                ? `Only ${[formatQty(short.haveMilli), l.unitSymbol].filter(Boolean).join(' ')} ${doc ? 'free to promise' : 'in stock'}`
                                : `${doc ? 'None free to promise' : 'Out of stock'} (${[formatQty(short.haveMilli), l.unitSymbol].filter(Boolean).join(' ')})`}
                            </div>
                          )
                        )
                      })()}
                      {l.discountPercentBp ? (
                        <div className="tabular text-xs font-medium text-primary">
                          {percentText(l.discountPercentBp)}% off: −
                          {money(l.discountPaisa ?? 0, settings)}
                        </div>
                      ) : null}
                      {belowMinPrice(l) !== null && (
                        <div className="text-xs font-medium text-danger">
                          Below minimum price {money(belowMinPrice(l) ?? 0, settings)}
                        </div>
                      )}
                      {aboveMrp(l) !== null && (
                        <div className="text-xs font-medium text-warning">
                          Above MRP: {money(aboveMrp(l) ?? 0, settings)} with VAT
                        </div>
                      )}
                      {l.trackSerials && l.productId && (
                        <SerialEntry
                          line={l}
                          onAdd={(serial) => addSerial(l.key, l.productId!, serial)}
                          onRemove={(serial) =>
                            dispatch({ type: 'removeSerial', key: l.key, serial })
                          }
                        />
                      )}
                      {l.trackBatches && l.productId && (
                        <BatchChoice
                          line={l}
                          index={cart.lines.indexOf(l)}
                          plan={batchPlan}
                          batches={batchStock.get(l.productId) ?? []}
                          alertDays={settings?.expiry_alert_days ?? 30}
                          onChoose={(batchId) =>
                            dispatch({ type: 'setBatch', key: l.key, batchId })
                          }
                        />
                      )}
                    </div>
                    <IconButton
                      label={`Remove ${l.description}`}
                      onClick={() => removeLine(l)}
                      className="justify-self-end sm:order-last lg:order-none xl:order-last"
                    >
                      <Trash2 className="size-4" />
                    </IconButton>
                    <div className="flex items-center overflow-hidden rounded-xl border border-border focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30">
                      {!l.trackSerials && (
                        <button
                          type="button"
                          aria-label={`Decrease ${l.description}`}
                          onClick={() => step(l, -1)}
                          className="flex size-9 shrink-0 items-center justify-center text-muted hover:bg-soft hover:text-ink"
                        >
                          <Minus className="size-4" />
                        </button>
                      )}
                      <QtyInput
                        aria-label={`Quantity of ${l.description}`}
                        milli={l.qtyMilli}
                        allowsDecimal={l.allowsDecimal}
                        onValue={(m) =>
                          m !== null && dispatch({ type: 'setQty', key: l.key, qtyMilli: m })
                        }
                        className="min-w-0 rounded-none border-0 px-1! text-center font-semibold tabular focus:ring-0"
                      />
                      {!l.trackSerials && (
                        <button
                          type="button"
                          aria-label={`Increase ${l.description}`}
                          onClick={() => step(l, 1)}
                          className="flex size-9 shrink-0 items-center justify-center text-muted hover:bg-soft hover:text-ink"
                        >
                          <Plus className="size-4" />
                        </button>
                      )}
                    </div>
                    <MoneyInput
                      aria-label={`Price of ${l.description}`}
                      className="px-2!"
                      paisa={l.unitPricePaisa}
                      onValue={(p) =>
                        p !== null && dispatch({ type: 'setPrice', key: l.key, unitPricePaisa: p })
                      }
                    />
                    <LineDiscountInput
                      description={l.description}
                      mode={modeOfLine(l)}
                      discountPaisa={l.discountPaisa ?? 0}
                      percentBp={l.discountPercentBp ?? null}
                      onAmount={(p) =>
                        dispatch({ type: 'setDiscount', key: l.key, discountPaisa: p })
                      }
                      onPercent={(bp) =>
                        dispatch({ type: 'setDiscountPercent', key: l.key, percentBp: bp })
                      }
                      onMode={(mode) => switchLineMode(l, mode)}
                    />
                  </li>
                ))}
              </ul>
            </fieldset>
          )}
        </Card>

        <Card className="hidden space-y-4 lg:sticky lg:top-0 lg:block">{checkout}</Card>
      </div>

      {/* Phones and tablets: the total and the Complete button stay in reach. */}
      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-2 border-t border-border bg-surface/95 px-3 pt-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur lg:hidden">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="flex min-h-12 min-w-0 flex-1 items-center justify-between gap-2 rounded-xl border border-border bg-surface px-3 text-left"
        >
          <span className="min-w-0">
            <span className="block truncate text-[11px] font-medium text-muted">
              {cart.lines.length} item{cart.lines.length === 1 ? '' : 's'} · {units} unit
              {units === 1 ? '' : 's'} · {customer?.name ?? 'Walk-in'}
            </span>
            <span className="tabular block text-lg font-extrabold">{totalText}</span>
          </span>
          <ChevronUp className="size-5 shrink-0 text-muted" aria-hidden />
        </button>
        <Button
          size="lg"
          className="shrink-0"
          loading={busy}
          disabled={cart.lines.length === 0 || !!totals.error}
          onClick={() => void (doc ? saveDoc(false) : complete())}
        >
          {doc ? 'Save' : 'Complete sale'}
        </Button>
      </div>
      <Modal
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={doc ? 'Details' : 'Checkout'}
      >
        <div className="space-y-4">{checkout}</div>
      </Modal>

      {qrOn && qrShown && payQr && (
        <PaymentQrScreen
          qr={payQr}
          amount={qrAmount}
          enter={qrAmount.replace(settings?.currency_symbol ?? 'Rs.', '').trim()}
          context={orderDetail?.tableName ?? undefined}
          busy={busy}
          onClose={() => setQrShown(false)}
          onReceived={() => {
            // Anything complete() has to ask ("Sell anyway?") shows over the bill, not here.
            setQrShown(false)
            void complete()
          }}
        />
      )}

      {customOpen && (
        <CustomItemDialog
          onClose={() => {
            setCustomOpen(false)
            focusScanner()
          }}
          onAdd={(item) => {
            dispatch({
              type: 'add',
              line: {
                productId: null,
                description: item.description,
                unitPricePaisa: item.unitPricePaisa,
                priceIncludesTax: item.priceIncludesTax,
                taxRateBp: item.taxRateBp,
                taxRateId: item.taxRateId,
                discountPaisa: 0,
                trackStock: false,
                unitSymbol: '',
                allowsDecimal: true,
                fixedPrice: true,
              },
              qtyMilli: item.qtyMilli,
              separate: true,
            })
            setReceived(undefined)
            setCustomOpen(false)
            focusScanner()
          }}
        />
      )}
      {newCustomer !== null && (
        <NewPartyDialog
          kind="customers"
          initialName={newCustomer}
          onClose={() => setNewCustomer(null)}
          onCreated={(c) => {
            setNewCustomer(null)
            chooseCustomer(c)
          }}
        />
      )}

      <Modal
        open={completed !== null}
        onClose={() => {
          setCompleted(null)
          if (orderId) navigate(tablesPath(orderDetail?.order))
          else if (source) navigate(salesOrderPath(source.order))
          else focusScanner()
        }}
        title="Sale complete"
        size="sm"
        footer={
          <Button
            onClick={() => {
              setCompleted(null)
              if (orderId) navigate(tablesPath(orderDetail?.order))
              else if (source) navigate(salesOrderPath(source.order))
              else focusScanner()
            }}
          >
            {orderId
              ? backLabel(orderDetail?.order)
              : source
                ? `Back to the ${KIND_WORDS[source.order.kind].one.toLowerCase()}`
                : 'New sale'}
          </Button>
        }
      >
        {completed && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-5 animate-scale-in text-success" />
              <span className="font-semibold">
                {completed.built.invoice.number ?? 'Invoice saved'}
              </span>
              {completed.serverNumbered && <Badge tone="warning">number on sync</Badge>}
            </div>
            {completed.check === 'checking' && (
              <p className="text-muted">Checking with the server…</p>
            )}
            {typeof completed.check === 'object' && completed.check !== null && (
              <Banner tone="danger">
                The server refused this bill: {issueText(completed.check.code).title.toLowerCase()}.
                Don’t hand it over yet: open Sync (top bar) to fix it.
              </Banner>
            )}
            <p className="animate-fade-in text-metric">
              {money(completed.built.invoice.total_paisa, settings)}
            </p>
            {completed.changePaisa > 0 && (
              <p>Change due: {money(completed.changePaisa, settings)}</p>
            )}
            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                variant="secondary"
                icon={<Printer className="size-4" />}
                disabled={completed.check === 'checking' || gettingNumber !== null}
                onClick={() => void printSale('receipt')}
              >
                {gettingNumber === 'receipt' ? 'Getting the bill number…' : 'Receipt'}
              </Button>
              <Button
                variant="secondary"
                icon={<Ellipsis className="size-4" />}
                disabled={completed.check === 'checking' || gettingNumber !== null}
                onClick={() => setMoreFor(completed.built.invoice.id)}
              >
                More…
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate(`/invoices/${completed.built.invoice.id}`)}
              >
                Open invoice
              </Button>
            </div>
          </div>
        )}
      </Modal>
      {moreFor && (
        <PrintDialog invoiceId={moreFor} serverName={serverName} onClose={() => setMoreFor(null)} />
      )}
      <ConfirmDialog
        open={shortfallAsk !== null}
        title="Sell more than you have in stock?"
        message={
          <span>
            {shortfallAsk?.map((x) => (
              <span key={x.productId} className="block">
                {x.description}: selling {formatQty(x.needMilli)}, only{' '}
                {formatQty(Math.max(0, x.haveMilli))} in stock.
              </span>
            ))}
            <span className="mt-2 block text-muted">
              Stock will go below zero. That usually means a delivery hasn’t been entered yet, or a
              count needs fixing.
            </span>
          </span>
        }
        confirmLabel="Sell anyway"
        onClose={() => setShortfallAsk(null)}
        onConfirm={() => {
          setShortfallAsk(null)
          void complete(true)
        }}
      />
    </div>
  )
}

function Row({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted">
        {label}
        {sub && <span className="tabular block text-xs">{sub}</span>}
      </dt>
      <dd className="tabular">{value}</dd>
    </div>
  )
}

function BatchChoice({
  line,
  index,
  plan,
  batches,
  alertDays,
  onChoose,
}: {
  line: CartLine
  index: number
  plan: BatchPlan
  batches: BatchWithStock[]
  alertDays: number
  onChoose: (batchId: string | null) => void
}) {
  const today = todayIso()
  const parts = plan.perLine[index] ?? []
  const label = (id: string | null | undefined) => {
    const b = batches.find((x) => x.batch.id === id)?.batch
    if (!b) return 'no batch'
    return `${b.batch_no}${b.expiry_date ? ` · exp ${b.expiry_date.slice(0, 7)}` : ''}`
  }
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
      <span className="text-muted">
        {parts.length > 1
          ? parts.map((p) => `${label(p.batchId)} (${p.qtyMilli / 1000})`).join(' + ')
          : label(parts[0]?.batchId)}
      </span>
      <Select
        dense
        aria-label={`Batch of ${line.description}`}
        value={line.batchId ?? ''}
        onChange={(e) => onChoose(e.target.value || null)}
      >
        <option value="">Earliest expiry first</option>
        {batches
          .filter((b) => b.qtyMilli > 0 || b.batch.id === line.batchId)
          .map((b) => {
            const badge = expiryBadge(b.batch.expiry_date, today, alertDays)
            return (
              <option key={b.batch.id} value={b.batch.id}>
                {label(b.batch.id)} · {b.qtyMilli / 1000} left
                {badge.status === 'expired'
                  ? ' · EXPIRED'
                  : badge.status === 'soon'
                    ? ' · soon'
                    : ''}
              </option>
            )
          })}
      </Select>
    </div>
  )
}

/** The serials of a serial-tracked line, and a box to type or scan more. */
function SerialEntry({
  line,
  onAdd,
  onRemove,
}: {
  line: CartLine
  /** Adds the serial to the line; resolves with why it could not, or null. */
  onAdd: (serial: string) => Promise<string | null>
  onRemove: (serial: string) => void
}) {
  const toast = useToast()
  const [text, setText] = useState('')
  const missing = line.qtyMilli / 1000 - (line.serials?.length ?? 0)
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
      {(line.serials ?? []).map((s) => (
        <span
          key={s}
          className="inline-flex items-center gap-1 rounded bg-soft px-1.5 py-0.5 font-mono"
        >
          {s}
          <button
            type="button"
            aria-label={`Remove serial ${s}`}
            className="text-muted hover:text-danger"
            onClick={() => onRemove(s)}
          >
            ×
          </button>
        </span>
      ))}
      {/* The scan button sits inside the box, so the pair never wraps apart on a phone. */}
      <div className="relative w-44">
        <Input
          dense
          aria-label={`Serial of ${line.description}`}
          placeholder={missing > 0 ? `Serial / IMEI (${missing} needed)` : 'Add serial / IMEI'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            e.stopPropagation()
            void onAdd(text).then((problem) => problem && toast.error(problem))
            setText('')
          }}
          className={cx('pr-8', missing > 0 && 'ring-1 ring-warning')}
        />
        <ScanButton
          mode="continuous"
          title="Scan a serial / IMEI"
          label={`Scan a serial for ${line.description}`}
          className="absolute right-0.5 top-1/2 size-7 -translate-y-1/2"
          onCode={async (code) => {
            const problem = await onAdd(code)
            return { ok: !problem, message: problem ?? `Serial ${code.toUpperCase()} added` }
          }}
        />
      </div>
    </div>
  )
}
